import {
  faCloudArrowDown,
  faEnvelope,
  faEarthEurope,
  faPaperPlane,
  faSpinner,
} from "@fortawesome/pro-regular-svg-icons";
import { closeAllDropdowns, DropDownItem } from "../../shared/DropDown.tsx";
import { ActivityShareOptions } from "./useActivityShareOptions.ts";

export type ShareAction =
  | "as_post"
  | "email_internal"
  | "email_external"
  | "pdf";

// Everything a share menu needs; built once per activity by useActivityShare
export type ActivityShareMenu = {
  options: ActivityShareOptions;
  emailRecommendationCount: { internal: number; external: number };
  pdfGenerating: boolean;
  onShare: (action: ShareAction) => void;
};

// The share targets of an activity as dropdown entries. Rendered in the
// "Teilen" submenu of the context menu and in the dropdown of the Teilen
// button below the activity, so both offer the same entries.
export default function ActivityShareMenuItems({
  share,
}: {
  share: ActivityShareMenu;
}) {
  const { options, emailRecommendationCount, pdfGenerating } = share;

  // Items with onSelect keep the click from the body, which would close the
  // menu: close it here, or it stays open behind the e-mail or PDF modal
  function onShare(action: ShareAction) {
    closeAllDropdowns();
    share.onShare(action);
  }

  function emailCount(type: "internal" | "external") {
    const count = emailRecommendationCount[type] ?? 0;
    return count > 0 ? (
      <span className="text-muted text-sm">{count}</span>
    ) : undefined;
  }

  return (
    <>
      {options.asPost && (
        <DropDownItem
          icon={faEarthEurope}
          text={I18n.t("js.activitystreams.share_dialog.as_post")}
          onSelect={() => onShare("as_post")}
          data={{ "data-testid": "share-option-as-post" }}
        />
      )}
      {options.emailInternal && (
        <DropDownItem
          icon={faEnvelope}
          text={I18n.t("js.activitystreams.share_dialog.email_internal")}
          additional={emailCount("internal")}
          onSelect={() => onShare("email_internal")}
          data={{ "data-testid": "share-option-email-internal" }}
        />
      )}
      {options.emailExternal && (
        <DropDownItem
          icon={faPaperPlane}
          text={I18n.t("js.activitystreams.share_dialog.email_external")}
          additional={emailCount("external")}
          onSelect={() => onShare("email_external")}
          data={{ "data-testid": "share-option-email-external" }}
        />
      )}
      {options.pdf && (
        <DropDownItem
          icon={pdfGenerating ? faSpinner : faCloudArrowDown}
          iconClassName={pdfGenerating ? "fa-spin" : undefined}
          text={I18n.t(
            pdfGenerating
              ? "js.activitystreams.share_dialog.pdf_generating"
              : "js.activitystreams.share_dialog.pdf",
          )}
          onSelect={() => onShare("pdf")}
          data={{ "data-testid": "share-option-pdf" }}
        />
      )}
    </>
  );
}
